import React, { useEffect } from 'react';
import {
    Container,
    Paper,
    Typography,
    Box,
    Divider,
    Card,
    CardContent,
    Avatar,
    Chip,
    useTheme,
    useMediaQuery,
    CssBaseline,
    ThemeProvider,
    createTheme,
    alpha,
    Grid,
    Button,
    Stack,
    IconButton,
} from '@mui/material';
import {
    School,
    Security,
    Email,
    Phone,
    LocationOn,
    Computer,
    People,
    Rocket,
    Timeline,
    Star,
    WorkspacePremium,
    SupportAgent,
    Cloud,
    Speed,
    IntegrationInstructions,
    Analytics,
    MobileFriendly,
    TrendingUp,
    EmojiEvents,
    Psychology,
    Handshake,
    Diversity3,
    Lightbulb,
    TrackChanges,
    Visibility,
    Verified,
    GpsFixed,
    ArrowBack,
} from '@mui/icons-material';

// Custom theme with JMK branding
const theme = createTheme({
    palette: {
        primary: {
            main: '#1a237e',
            light: '#3949ab',
            dark: '#0d1445',
        },
        secondary: {
            main: '#ff6f00',
            light: '#ffa040',
            dark: '#c43e00',
        },
        background: {
            default: '#f5f7fa',
            paper: '#ffffff',
        },
        info: {
            main: '#0288d1',
            light: '#4fc3f7',
        },
        success: {
            main: '#2e7d32',
        },
        warning: {
            main: '#e65100',
        },
    },
    typography: {
        fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
        h2: {
            fontWeight: 700,
            letterSpacing: '-0.02em',
        },
        h3: {
            fontWeight: 600,
            letterSpacing: '-0.01em',
        },
        h4: {
            fontWeight: 600,
            letterSpacing: '-0.01em',
        },
        h5: {
            fontWeight: 500,
        },
        h6: {
            fontWeight: 500,
        },
    },
    shape: {
        borderRadius: 12,
    },
    components: {
        MuiPaper: {
            styleOverrides: {
                root: {
                    backgroundImage: 'none',
                },
            },
        },
    },
});

// Company values
const values = [
    {
        icon: <Diversity3 />,
        title: 'Collaboration',
        description: 'Working together with schools to create better educational outcomes.',
        color: '#1565c0',
    },
    {
        icon: <Lightbulb />,
        title: 'Innovation',
        description: 'Continuously evolving our platform with cutting-edge technology.',
        color: '#ff6f00',
    },
    {
        icon: <Verified />,
        title: 'Integrity',
        description: 'Operating with transparency, honesty, and ethical practices.',
        color: '#2e7d32',
    },
    {
        icon: <Psychology />,
        title: 'Excellence',
        description: 'Delivering high-quality solutions that exceed expectations.',
        color: '#6a1b9a',
    },
    {
        icon: <GpsFixed />,
        title: 'Impact',
        description: 'Making a meaningful difference in education through technology.',
        color: '#c62828',
    },
    {
        icon: <Handshake />,
        title: 'Trust',
        description: 'Building lasting relationships through reliability and support.',
        color: '#00695c',
    },
];

// Features
const features = [
    {
        icon: <Cloud />,
        title: 'Cloud-Based',
        description: 'Access your school data anytime, anywhere with our secure cloud platform.',
        color: '#0288d1',
    },
    {
        icon: <Security />,
        title: 'Enterprise Security',
        description: 'Bank-grade encryption and security protocols protect your sensitive data.',
        color: '#1a237e',
    },
    {
        icon: <Speed />,
        title: 'High Performance',
        description: 'Lightning-fast response times with optimized infrastructure.',
        color: '#ff6f00',
    },
    {
        icon: <IntegrationInstructions />,
        title: 'Seamless Integration',
        description: 'Connect with payment gateways, SMS services, and more.',
        color: '#2e7d32',
    },
    {
        icon: <Analytics />,
        title: 'Advanced Analytics',
        description: 'Powerful reporting and insights to drive better decisions.',
        color: '#6a1b9a',
    },
    {
        icon: <MobileFriendly />,
        title: 'Mobile Ready',
        description: 'Responsive design works on desktops, tablets, and smartphones.',
        color: '#c62828',
    },
];

// Team members
const team = [
    {
        name: 'Dr. Mahesh Kulkarni',
        role: 'Founder & CEO',
        expertise: 'EdTech Strategy & Innovation',
        avatar: 'MK',
        color: '#1a237e',
    },
    {
        name: 'Priya Sharma',
        role: 'Chief Technology Officer',
        expertise: 'Cloud Architecture & AI',
        avatar: 'PS',
        color: '#ff6f00',
    },
    {
        name: 'Rahul Deshmukh',
        role: 'Head of Product',
        expertise: 'User Experience & Design',
        avatar: 'RD',
        color: '#2e7d32',
    },
    {
        name: 'Dr. Sneha Patil',
        role: 'Education Consultant',
        expertise: 'Curriculum & Pedagogy',
        avatar: 'SP',
        color: '#6a1b9a',
    },
    {
        name: 'Vikram Singh',
        role: 'Lead Developer',
        expertise: 'Full Stack Engineering',
        avatar: 'VS',
        color: '#c62828',
    },
    {
        name: 'Anjali Nair',
        role: 'Customer Success Manager',
        expertise: 'Client Relations & Support',
        avatar: 'AN',
        color: '#00695c',
    },
];

// Milestones - Updated to start from 2021
const milestones = [
    {
        year: '2021',
        title: 'Founded',
        description: 'JMKINFO was established with a vision to revolutionize school management.',
        icon: <Rocket />,
    },
    {
        year: '2021',
        title: 'First Launch',
        description: 'Launched the initial version of the School Management System.',
        icon: <Computer />,
    },
    {
        year: '2022',
        title: '100+ Schools',
        description: 'Reached 100+ schools across India using our platform.',
        icon: <School />,
    },
    {
        year: '2022',
        title: 'Advanced Features',
        description: 'Introduced AI-powered analytics and automated reporting.',
        icon: <TrendingUp />,
    },
    {
        year: '2023',
        title: '500+ Schools',
        description: 'Surpassed 500 schools with enhanced cloud infrastructure.',
        icon: <Cloud />,
    },
    {
        year: '2024',
        title: 'Global Expansion',
        description: 'Expanded services to international educational institutions.',
        icon: <Timeline />,
    },
    {
        year: '2024',
        title: 'Innovation Award',
        description: 'Recognized for excellence in educational technology innovation.',
        icon: <EmojiEvents />,
    },
    {
        year: '2025',
        title: '1000+ Schools',
        description: 'Proudly serving 1000+ schools with comprehensive management solutions.',
        icon: <WorkspacePremium />,
    },
];

// Stats
const stats = [
    { value: '1000+', label: 'Schools Trust Us', icon: <School /> },
    { value: '1000+', label: 'Active Users', icon: <People /> },
    { value: '98%', label: 'Satisfaction Rate', icon: <Star /> },
    { value: '24/7', label: 'Customer Support', icon: <SupportAgent /> },
];

// Disable right-click globally
const disableRightClick = (e) => {
    e.preventDefault();
    return false;
};

// Disable keyboard shortcuts
const disableKeyboardShortcuts = (e) => {
    if (
        (e.ctrlKey && ['c', 'C', 'a', 'A', 'u', 'U', 's', 'S'].includes(e.key)) ||
        e.key === 'F12' ||
        (e.ctrlKey && e.shiftKey && ['i', 'I', 'j', 'J'].includes(e.key)) ||
        (e.metaKey && ['c', 'C', 'a', 'A', 'u', 'U', 's', 'S'].includes(e.key))
    ) {
        e.preventDefault();
        return false;
    }
};

// Disable text selection
const disableTextSelection = (e) => {
    const target = e.target;
    if (target && target.closest && target.closest('.no-select')) {
        e.preventDefault();
        return false;
    }
};

const AboutUs = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    useEffect(() => {
        document.addEventListener('contextmenu', disableRightClick);
        document.addEventListener('keydown', disableKeyboardShortcuts);
        document.addEventListener('selectstart', disableTextSelection);
        document.addEventListener('dragstart', disableTextSelection);

        const preventCopy = (e) => e.preventDefault();
        document.addEventListener('copy', preventCopy);
        document.addEventListener('cut', preventCopy);

        return () => {
            document.removeEventListener('contextmenu', disableRightClick);
            document.removeEventListener('keydown', disableKeyboardShortcuts);
            document.removeEventListener('selectstart', disableTextSelection);
            document.removeEventListener('dragstart', disableTextSelection);
            document.removeEventListener('copy', preventCopy);
            document.removeEventListener('cut', preventCopy);
        };
    }, []);

    const handleBack = () => {
        window.history.back();
    };

    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <Box
                className="no-select"
                sx={{
                    userSelect: 'none',
                    WebkitUserSelect: 'none',
                    MozUserSelect: 'none',
                    msUserSelect: 'none',
                    minHeight: '100vh',
                    backgroundColor: 'background.default',
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                {/* Back Button - Floating */}
                {/* <Box
                    sx={{
                        position: 'sticky',
                        top: 16,
                        zIndex: 100,
                        px: { xs: 2, sm: 3, md: 4 },
                        pt: 2,
                    }}
                >
                    <IconButton
                        onClick={handleBack}
                        sx={{
                            backgroundColor: alpha(theme.palette.primary.main, 0.08),
                            color: theme.palette.primary.main,
                            '&:hover': {
                                backgroundColor: alpha(theme.palette.primary.main, 0.15),
                                transform: 'scale(1.05)',
                            },
                            transition: 'all 0.2s',
                            boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                        }}
                    >
                        <ArrowBack />
                    </IconButton>
                </Box> */}

                {/* Content */}
                <Container
                    maxWidth="lg"
                    sx={{
                        flex: 1,
                        py: { xs: 2, md: 3 },
                        px: { xs: 2, sm: 3, md: 4 },
                    }}
                >
                    {/* Hero Section */}
                    <Paper
                        elevation={0}
                        sx={{
                            position: 'relative',
                            overflow: 'hidden',
                            borderRadius: 4,
                            mb: 5,
                            border: '1px solid #efededa5',
                            // background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 50%, ${theme.palette.primary.light} 100%)`,
                            p: { xs: 4, sm: 5, md: 6 },
                        }}
                    >
                        <Box sx={{ maxWidth: '800px', position: 'relative', zIndex: 2 }}>
                            {/* <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                                <Chip
                                    label="About JMKINFO"
                                    sx={{
                                        backgroundColor: alpha(theme.palette.secondary.main, 0.2),
                                        color: '#aa8802',
                                        fontWeight: 600,
                                        border: `1px solid ${alpha('#aa8802', 0.2)}`,
                                    }}
                                />
                                <Chip
                                    label="Started 2021"
                                    sx={{
                                        backgroundColor: alpha(theme.palette.info.main, 0.2),
                                        color: '#aa8802',
                                        fontWeight: 600,
                                        border: `1px solid ${alpha('#aa8802', 0.2)}`,
                                    }}
                                />
                            </Stack> */}
                            <Typography
                                variant="h2"
                                sx={{
                                    color: 'goldenrod',
                                    fontSize: { xs: '2rem', sm: '2.5rem', md: '3.5rem' },
                                    fontWeight: 700,
                                    mb: 2,
                                    lineHeight: 1.1,
                                }}
                            >
                                Transforming Education
                                <br />
                                <span style={{ color: theme.palette.primary.main }}>
                                    Through Technology
                                </span>
                            </Typography>
                            <Typography
                                variant="h6"
                                sx={{
                                    color: alpha('#002a3b', 0.85),
                                    mb: 3,
                                    fontWeight: 400,
                                    lineHeight: 1.6,
                                    maxWidth: '600px',
                                }}
                            >
                                JMKinfo is dedicated to empowering educational institutions with
                                innovative, cloud-based management solutions that streamline operations
                                and enhance learning outcomes.
                            </Typography>
                            {/* <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                <Button
                                    variant="contained"
                                    sx={{
                                        backgroundColor: theme.palette.secondary.main,
                                        color: '#fff',
                                        '&:hover': { backgroundColor: theme.palette.secondary.dark },
                                        px: 4,
                                        py: 1.5,
                                    }}
                                >
                                    Our Mission
                                </Button>
                                <Button
                                    variant="outlined"
                                    sx={{
                                        borderColor: alpha('#fff', 0.4),
                                        color: '#fff',
                                        '&:hover': { borderColor: '#fff', backgroundColor: alpha('#fff', 0.05) },
                                    }}
                                >
                                    Contact Us
                                </Button>
                            </Stack> */}
                        </Box>
                        {/* Decorative elements */}
                        <Box
                            sx={{
                                position: 'absolute',
                                right: -50,
                                top: -50,
                                opacity: 0.08,
                                transform: 'rotate(15deg)',
                            }}
                        >
                            <School sx={{ fontSize: 300, color: '#fff' }} />
                        </Box>
                    </Paper>

                    {/* Stats Section */}
                    <Grid container spacing={3} sx={{ mb: 5 }}>
                        {stats.map((stat, idx) => (
                            <Grid item xs={6} sm={3} key={idx}>
                                <Card
                                    elevation={0}
                                    sx={{
                                        textAlign: 'center',
                                        p: 3,
                                        borderRadius: 3,
                                        border: `1px solid ${alpha(theme.palette.primary.main, 0.06)}`,
                                        transition: 'transform 0.2s',
                                        '&:hover': { transform: 'translateY(-4px)' },
                                    }}
                                >
                                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                width: 48,
                                                height: 48,
                                                borderRadius: 2,
                                                backgroundColor: alpha(theme.palette.primary.main, 0.08),
                                                color: '#0f3b4c',
                                            }}
                                        >
                                            {stat.icon}
                                        </Box>
                                    </Box>
                                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#0f3b4c' }}>
                                        {stat.value}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                                        {stat.label}
                                    </Typography>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>

                    {/* Our Story */}
                    <Paper
                        elevation={0}
                        sx={{
                            p: { xs: 3, sm: 4 },
                            mb: 4,
                            borderRadius: 3,
                            border: `1px solid ${alpha(theme.palette.primary.main, 0.06)}`,
                        }}
                    >
                        <Typography variant="h4" sx={{ fontWeight: 600, color: '#0f3b4c', mb: 3 }}>
                            Our Story
                        </Typography>
                        <Grid container spacing={4}>
                            <Grid item xs={12} md={6}>
                                <Typography variant="body1" sx={{ color: theme.palette.text.secondary, lineHeight: 1.8, mb: 2 }}>
                                    Founded in 2021, JMKINFO began with a simple yet powerful vision:
                                    to create a comprehensive, user-friendly school management system
                                    that would simplify administrative tasks and enhance the educational experience.
                                </Typography>
                                <Typography variant="body1" sx={{ color: theme.palette.text.secondary, lineHeight: 1.8 }}>
                                    What started as a small project in Nagpur has grown into a trusted
                                    platform serving over 1000 schools across India and beyond. Our journey
                                    has been driven by a passion for education and a commitment to leveraging
                                    technology for positive change.
                                </Typography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Box
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(2, 1fr)',
                                        gap: 2,
                                        gridAutoFlow: 'column',
                                    }}
                                >
                                    {milestones.slice(0, 4).map((milestone, idx) => (
                                        <Card
                                            key={idx}
                                            elevation={0}
                                            sx={{
                                                p: 2,
                                                borderRadius: 2,
                                                border: `1px solid ${alpha(theme.palette.primary.main, 0.06)}`,
                                                textAlign: 'center',
                                                transition: 'all 0.2s',
                                                '&:hover': {
                                                    boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.08)}`,
                                                    borderColor: theme.palette.primary.main,
                                                    transform: 'translateY(-4px)',
                                                },
                                            }}
                                        >
                                            <Box sx={{ color: '#0f3b4c', mb: 1 }}>
                                                {milestone.icon}
                                            </Box>
                                            <Typography variant="caption" sx={{ color: theme.palette.primary.main, fontWeight: 700 }}>
                                                {milestone.year}
                                            </Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5 }}>
                                                {milestone.title}
                                            </Typography>
                                        </Card>
                                    ))}
                                </Box>
                            </Grid>
                        </Grid>
                    </Paper>

                    {/* Mission & Vision */}
                    <Grid container spacing={3} sx={{ mb: 4 }}>
                        <Grid item xs={12} md={6}>
                            <Card
                                elevation={0}
                                sx={{
                                    p: 3,
                                    borderRadius: 3,
                                    border: `1px solid ${alpha(theme.palette.primary.main, 0.06)}`,
                                    height: '100%',
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                    <TrackChanges sx={{ color: '#0f3b4c', mr: 1.5 }} />
                                    <Typography variant="h5" sx={{ fontWeight: 600, color: '#0f3b4c' }}>
                                        Our Mission
                                    </Typography>
                                </Box>
                                <Typography variant="body1" sx={{ color: theme.palette.text.secondary, lineHeight: 1.8 }}>
                                    To empower educational institutions with intuitive, secure, and
                                    comprehensive technology solutions that streamline administration,
                                    improve communication, and enable data-driven decision-making.
                                </Typography>
                            </Card>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Card
                                elevation={0}
                                sx={{
                                    p: 3,
                                    borderRadius: 3,
                                    border: `1px solid ${alpha(theme.palette.secondary.main, 0.06)}`,
                                    height: '100%',
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                    <Visibility sx={{ color: theme.palette.secondary.main, mr: 1.5 }} />
                                    <Typography variant="h5" sx={{ fontWeight: 600, color: theme.palette.secondary.main }}>
                                        Our Vision
                                    </Typography>
                                </Box>
                                <Typography variant="body1" sx={{ color: theme.palette.text.secondary, lineHeight: 1.8 }}>
                                    To be the most trusted and innovative educational management platform
                                    globally, transforming how schools operate and enabling better
                                    outcomes for students, teachers, and administrators.
                                </Typography>
                            </Card>
                        </Grid>
                    </Grid>

                    {/* Our Values */}
                    <Typography variant="h4" sx={{ fontWeight: 600, color: '#0f3b4c', mb: 3, mt: 2 }}>
                        Our Core Values
                    </Typography>
                    <Grid container spacing={3} sx={{ mb: 5 }}>
                        {values.map((value, idx) => (
                            <Grid item xs={12} sm={6} md={4} key={idx}>
                                <Card
                                    elevation={0}
                                    sx={{
                                        p: 3,
                                        borderRadius: 3,
                                        border: `1px solid ${alpha(value.color, 0.12)}`,
                                        height: '100%',
                                        transition: 'all 0.2s',
                                        '&:hover': {
                                            boxShadow: `0 8px 32px ${alpha(value.color, 0.08)}`,
                                            borderColor: value.color,
                                            transform: 'translateY(-4px)',
                                        },
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            width: 56,
                                            height: 56,
                                            borderRadius: 2,
                                            backgroundColor: alpha(value.color, 0.1),
                                            color: value.color,
                                            mb: 2,
                                        }}
                                    >
                                        {value.icon}
                                    </Box>
                                    <Typography variant="h6" sx={{ fontWeight: 600, color: value.color, mb: 1 }}>
                                        {value.title}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.6 }}>
                                        {value.description}
                                    </Typography>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>

                    {/* Features */}
                    <Typography variant="h4" sx={{ fontWeight: 600, color: '#0f3b4c', mb: 3 }}>
                        Why Choose JMKINFO?
                    </Typography>
                    <Grid container spacing={3} sx={{ mb: 5 }}>
                        {features.map((feature, idx) => (
                            <Grid item xs={12} sm={6} md={4} key={idx}>
                                <Card
                                    elevation={0}
                                    sx={{
                                        p: 3,
                                        borderRadius: 3,
                                        border: `1px solid ${alpha(feature.color, 0.1)}`,
                                        height: '100%',
                                        transition: 'all 0.2s',
                                        '&:hover': {
                                            boxShadow: '0 8px 32px rgba(0,0,0,0.06)',
                                            transform: 'translateY(-4px)',
                                        },
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            width: 56,
                                            height: 56,
                                            borderRadius: 2,
                                            backgroundColor: alpha(feature.color, 0.1),
                                            color: feature.color,
                                            mb: 2,
                                        }}
                                    >
                                        {feature.icon}
                                    </Box>
                                    <Typography variant="h6" sx={{ fontWeight: 600, color: feature.color, mb: 1 }}>
                                        {feature.title}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.6 }}>
                                        {feature.description}
                                    </Typography>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>

                    {/* Journey Timeline */}
                    <Typography variant="h4" sx={{ fontWeight: 600, color: '#0f3b4c', mb: 3 }}>
                        Our Journey
                    </Typography>
                    <Paper
                        elevation={0}
                        sx={{
                            p: { xs: 3, sm: 4 },
                            mb: 5,
                            borderRadius: 3,
                            border: `1px solid ${alpha(theme.palette.primary.main, 0.06)}`,
                        }}
                    >
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(4, 1fr)' },
                                gap: 3,
                            }}
                        >
                            {milestones.map((milestone, idx) => (
                                <Box key={idx} sx={{ textAlign: 'center' }}>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            width: 64,
                                            height: 64,
                                            borderRadius: '50%',
                                            backgroundColor: alpha(theme.palette.primary.main, 0.08),
                                            color: '#0f3b4c',
                                            mx: 'auto',
                                            mb: 1.5,
                                            fontSize: 28,
                                        }}
                                    >
                                        {milestone.icon}
                                    </Box>
                                    <Typography variant="h6" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
                                        {milestone.year}
                                    </Typography>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 0.5 }}>
                                        {milestone.title}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: 'block' }}>
                                        {milestone.description}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    </Paper>

                    {/* Team Section */}
                    {/* <Typography variant="h4" sx={{ fontWeight: 600, color: theme.palette.primary.main, mb: 3 }}>
            Meet Our Team
          </Typography>
          <Grid container spacing={3} sx={{ mb: 4 }}>
            {team.map((member, idx) => (
              <Grid item xs={12} sm={6} md={4} key={idx}>
                <Card
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: 3,
                    border: `1px solid ${alpha(member.color, 0.12)}`,
                    textAlign: 'center',
                    transition: 'all 0.2s',
                    '&:hover': {
                      boxShadow: `0 8px 32px ${alpha(member.color, 0.08)}`,
                      transform: 'translateY(-4px)',
                    },
                  }}
                >
                  <Avatar
                    sx={{
                      width: 80,
                      height: 80,
                      mx: 'auto',
                      mb: 2,
                      bgcolor: member.color,
                      fontSize: 28,
                      fontWeight: 600,
                    }}
                  >
                    {member.avatar}
                  </Avatar>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {member.name}
                  </Typography>
                  <Typography
                    variant="subtitle2"
                    sx={{ color: member.color, fontWeight: 500, mb: 0.5 }}
                  >
                    {member.role}
                  </Typography>
                  <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                    {member.expertise}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid> */}

                    {/* Contact Section */}
                    <Paper
                        elevation={0}
                        sx={{
                            p: { xs: 3, sm: 4 },
                            borderRadius: 3,
                            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.03)} 0%, ${alpha(theme.palette.secondary.main, 0.03)} 100%)`,
                            border: `1px solid ${alpha(theme.palette.primary.main, 0.06)}`,
                        }}
                    >
                        <Grid container spacing={3} alignItems="center">
                            <Grid item xs={12} md={8}>
                                <Typography variant="h5" sx={{ fontWeight: 600, color: '#0f3b4c', mb: 1 }}>
                                    Join Us in Transforming Education
                                </Typography>
                                <Typography variant="body1" sx={{ color: theme.palette.text.secondary }}>
                                    Whether you're a school looking for better management solutions or
                                    an educator passionate about technology in education, we'd love to hear from you.
                                </Typography>
                                {/* <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 2 }}>
                                    <Button
                                        variant="contained"
                                        sx={{
                                            backgroundColor: theme.palette.secondary.main,
                                            '&:hover': { backgroundColor: theme.palette.secondary.dark },
                                        }}
                                    >
                                        Get Started
                                    </Button>
                                    <Button variant="outlined" startIcon={<Email />}>
                                        Contact Us
                                    </Button>
                                </Stack> */}
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <LocationOn sx={{ color: '#0f3b4c', fontSize: 20 }} />
                                        <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                                            Nagpur, Maharashtra, India
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Email sx={{ color: '#0f3b4c', fontSize: 20 }} />
                                        <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                                            jmk.infotech2021@gmail.com
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Phone sx={{ color: '#0f3b4c', fontSize: 20 }} />
                                        <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                                            +91 97306 76527
                                        </Typography>
                                    </Box>
                                </Box>
                            </Grid>
                        </Grid>
                    </Paper>

                    {/* Footer */}
                    <Box
                        component="footer"
                        sx={{
                            mt: 5,
                            pt: 4,
                            pb: 2,
                            textAlign: 'center',
                            borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
                        }}
                    >
                        <Typography
                            variant="body2"
                            sx={{
                                color: theme.palette.text.secondary,
                                fontWeight: 400,
                                letterSpacing: '0.3px',
                            }}
                        >
                            © {new Date().getFullYear()} JMKINFO School Management System. All rights reserved.
                        </Typography>
                        <Typography
                            variant="caption"
                            sx={{
                                color: alpha(theme.palette.text.secondary, 0.6),
                                display: 'block',
                                mt: 0.5,
                                fontStyle: 'italic',
                            }}
                        >
                            Empowering Education Through Technology | Started 2021
                        </Typography>
                    </Box>
                </Container>
            </Box>
        </ThemeProvider>
    );
};

export default AboutUs;